import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useNavigate, useLocation, Link, Outlet } from "react-router-dom";
import {
  Calendar,
  FileChartPie,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  ChartPie,
  Users,
  GitBranch,
  ChevronDown,
  BriefcaseBusiness,
  Megaphone,
} from "lucide-react";
import { useAuth } from "../hooks/use-auth";
import { useToast } from "../contexts/toast/toast-context";
import {
  APP_RESOURCES,
  AccessControl,
  type AppResource,
} from "../permissions/access-control";
import { NotificationCenter } from "../components/notification-center";

import { useTenantFeatures } from "../contexts/tenant-features";

export default function AuthenticatedLayout() {
  const { protocolsEnabled } = useTenantFeatures();
  const { logout, user } = useAuth();
  const { info } = useToast();
  const canView = (resource: AppResource) =>
    AccessControl.canView(resource, user?.role);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches;
  });
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isAttendancesActive =
    isActive("/painel/conversas") ||
    isActive("/painel/dashboard-funil") ||
    isActive("/painel/report");
  const canViewAttendances =
    canView(APP_RESOURCES.PANEL_CONVERSATIONS) ||
    canView(APP_RESOURCES.PANEL_DASHBOARD_FUNNEL) ||
    canView(APP_RESOURCES.PANEL_REPORT);
  const [attendancesOpen, setAttendancesOpen] = useState(isAttendancesActive);
  const isCommercialActive = isActive("/painel/comercial");
  const [commercialOpen, setCommercialOpen] = useState(isCommercialActive);
  const menuNavigationRef = useRef(false);

  useEffect(() => {
    if (menuNavigationRef.current) {
      menuNavigationRef.current = false;
      return;
    }

    if (isAttendancesActive) {
      setAttendancesOpen(true);
    }
    if (isCommercialActive) {
      setCommercialOpen(true);
    }
  }, [isAttendancesActive, isCommercialActive, location.pathname]);

  const handleMenuItemClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    menuNavigationRef.current =
      event.currentTarget.pathname !== location.pathname;
    setAttendancesOpen(false);
    setCommercialOpen(false);
    setCollapsed(true);
  };

  const handleLogout = async () => {
    await logout();
    info("Até logo!");
    navigate("/");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100 supports-[height:100dvh]:h-[100dvh]">
      {/* Sidebar */}
      <div
        className={`app-shell-sidebar transition-all duration-300 ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        <div className="border-b border-blue-900 p-3">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/painel/dashboard"
              aria-label="Sengia"
              onClick={handleMenuItemClick}
            >
              <img
                src={
                  collapsed
                    ? "/img/sengia-symbol-white.png"
                    : "/img/sengia-logo-horizontal-dark.png"
                }
                alt="Sengia"
                className={
                  collapsed
                    ? "h-8 w-8 object-contain"
                    : "h-8 w-36 object-contain object-left"
                }
              />
            </Link>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="cursor-pointer rounded-md p-1 text-slate-100 transition hover:bg-blue-900 hover:text-white"
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              <Menu size={20} />
            </button>
          </div>
          {!collapsed && (
            <p className="mt-3 truncate text-sm text-slate-200">
              Olá, {user?.name?.split(" ")[0]}
            </p>
          )}
        </div>

        <nav className="flex-1 px-2 py-4 space-y-2 text-sm">
          {canView(APP_RESOURCES.PANEL_DASHBOARD) && (
            <Link
              to="/painel/dashboard"
              onClick={handleMenuItemClick}
              className={`app-shell-sidebar-link ${
                isActive("/painel/dashboard")
                  ? "app-shell-sidebar-link-active"
                  : ""
              }`}
            >
              <ChartPie size={18} />
              {!collapsed && <span>Dashboard</span>}
            </Link>
          )}

          {canViewAttendances && (
            <div>
              <button
                type="button"
                onClick={() => {
                  if (collapsed) {
                    setCollapsed(false);
                    setAttendancesOpen(true);
                    return;
                  }
                  setAttendancesOpen((open) => !open);
                }}
                className={`app-shell-sidebar-link w-full cursor-pointer ${
                  isAttendancesActive ? "app-shell-sidebar-link-active" : ""
                }`}
                aria-expanded={!collapsed && attendancesOpen}
                aria-controls="attendances-submenu"
              >
                <MessageSquare size={18} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Atendimentos</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${attendancesOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </button>

              {!collapsed && attendancesOpen && (
                <div id="attendances-submenu" className="mt-1 space-y-1 pl-4">
                  {canView(APP_RESOURCES.PANEL_CONVERSATIONS) && (
                    <Link
                      to="/painel/conversas"
                      onClick={handleMenuItemClick}
                      className={`app-shell-sidebar-link text-xs ${
                        isActive("/painel/conversas")
                          ? "app-shell-sidebar-link-active"
                          : ""
                      }`}
                    >
                      <MessageSquare size={18} />
                      <span>Painel</span>
                    </Link>
                  )}

                  {canView(APP_RESOURCES.PANEL_DASHBOARD_FUNNEL) && (
                    <Link
                      to="/painel/dashboard-funil"
                      onClick={handleMenuItemClick}
                      className={`app-shell-sidebar-link text-xs ${
                        isActive("/painel/dashboard-funil")
                          ? "app-shell-sidebar-link-active"
                          : ""
                      }`}
                    >
                      <GitBranch size={18} />
                      <span>Esteira</span>
                    </Link>
                  )}

                  {canView(APP_RESOURCES.PANEL_REPORT) && (
                    <Link
                      to="/painel/report"
                      onClick={handleMenuItemClick}
                      className={`app-shell-sidebar-link text-xs ${
                        isActive("/painel/report")
                          ? "app-shell-sidebar-link-active"
                          : ""
                      }`}
                    >
                      <FileChartPie size={18} />
                      <span>Relatórios</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {(canView(APP_RESOURCES.PANEL_COMMERCIAL_FUNNEL) ||
            canView(APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS)) && (
            <div>
              <button
                type="button"
                onClick={() => {
                  if (collapsed) {
                    setCollapsed(false);
                    setCommercialOpen(true);
                    return;
                  }
                  setCommercialOpen((open) => !open);
                }}
                className={`app-shell-sidebar-link w-full cursor-pointer ${
                  isCommercialActive ? "app-shell-sidebar-link-active" : ""
                }`}
                aria-expanded={!collapsed && commercialOpen}
                aria-controls="commercial-submenu"
              >
                <BriefcaseBusiness size={18} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Comercial</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${commercialOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </button>
              {!collapsed && commercialOpen && (
                <div id="commercial-submenu" className="mt-1 space-y-1 pl-4">
                  {canView(APP_RESOURCES.PANEL_COMMERCIAL_FUNNEL) && (
                    <Link
                      to="/painel/comercial/funil"
                      onClick={handleMenuItemClick}
                      className={`app-shell-sidebar-link text-xs ${
                        isActive("/painel/comercial/funil")
                          ? "app-shell-sidebar-link-active"
                          : ""
                      }`}
                    >
                      <GitBranch size={18} />
                      <span>Funil de vendas</span>
                    </Link>
                  )}
                  {canView(APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS) && (
                    <Link
                      to="/painel/comercial/campanhas"
                      onClick={handleMenuItemClick}
                      className={`app-shell-sidebar-link text-xs ${
                        isActive("/painel/comercial/campanhas")
                          ? "app-shell-sidebar-link-active"
                          : ""
                      }`}
                    >
                      <Megaphone size={18} />
                      <span>Campanhas</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {protocolsEnabled && canView(APP_RESOURCES.PANEL_PROTOCOLS) && (
            <Link
              to="/painel/protocolos"
              onClick={handleMenuItemClick}
              className={`app-shell-sidebar-link ${isActive("/painel/protocolos") ? "app-shell-sidebar-link-active" : ""}`}
            >
              <FileChartPie size={18} />
              {!collapsed && <span>Protocolos</span>}
            </Link>
          )}
          {canView(APP_RESOURCES.PANEL_APPOINTMENTS) && (
            <Link
              to="/painel/appointment"
              onClick={handleMenuItemClick}
              className={`app-shell-sidebar-link ${
                isActive("/painel/appointment")
                  ? "app-shell-sidebar-link-active"
                  : ""
              }`}
            >
              <Calendar size={18} />
              {!collapsed && <span>Agendamentos</span>}
            </Link>
          )}

          {canView(APP_RESOURCES.PANEL_CLIENTS) && (
            <Link
              to="/painel/contatos"
              onClick={handleMenuItemClick}
              className={`app-shell-sidebar-link ${
                isActive("/painel/contatos")
                  ? "app-shell-sidebar-link-active"
                  : ""
              }`}
            >
              <Users size={18} />
              {!collapsed && <span>Contatos</span>}
            </Link>
          )}

          {canView(APP_RESOURCES.PANEL_CONFIG) && (
            <Link
              to="/painel/config"
              onClick={handleMenuItemClick}
              className={`app-shell-sidebar-link ${
                isActive("/painel/config")
                  ? "app-shell-sidebar-link-active"
                  : ""
              }`}
            >
              <Settings size={18} />
              {!collapsed && <span>Configurações</span>}
            </Link>
          )}
        </nav>
      </div>

      {/* Conteúdo principal */}
      <div className="flex min-w-0 flex-1 flex-col min-h-0">
        {/* Topbar */}
        <header className="app-shell-topbar">
          <NotificationCenter />
          <button
            onClick={handleLogout}
            className="btn btn-ghost cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </header>

        {/* Área de conteúdo */}
        <main className="app-shell-main p-2">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
